<script setup>
import {ref, watch, onMounted, computed, defineModel} from 'vue';
import moment from 'moment';
import api from '../api';
import useStore from './../store';
import {storeToRefs} from "pinia";

// Props definition
const props = defineProps({
  attraction: {
    type: [Object, Boolean],
    default: () => ({}),
  },
  items: {
    type: Array,
    default: () => [],
  },
});

const model = defineModel({
  type: Array,
  default: () => [],
});

// Emits definition
const emit = defineEmits(['details']);

// Composition setup
const store = useStore();
const { options } = storeToRefs(store);

// Reactive state
const isOpen = ref(false);
const voucher = ref('');
const current = ref(model.value);
const details = ref([]);
const loading = ref(false);
const errors = ref([]);

// Methods
const toggleOpen = () => {
  isOpen.value = !isOpen.value;
};

const human = (voucher) => {
  if (voucher.valueType === 'PERCENT' && voucher.value) {
    return `Valid coupon ${voucher.value}% off`;
  }

  if (voucher.valueType === 'VALUE' && voucher.value) {
    return `Valid coupon $${voucher.value.toFixed(2)} off`;
  }

  if (voucher.valueType === 'PRODUCT') {
    return 'Valid coupon';
  }

  return '';
};

const check = () => {
  const code = String(voucher.value ?? '').trim();

  if (loading.value || code === '') return;
  
  if (current.value.includes(code)) {
    voucher.value = '';
    return;
  }

  errors.value = [];
  loading.value = true;

  api.voucher({
    coupon: code,
    attraction: props.attraction.id,
  })
      .then((result) => {
        if (['ISSUED', 'PARTIALLY_REDEEMED'].includes(result.status)) {
          current.value.push(result.code ?? code);
          details.value.push(result);
          model.value = current.value;
          emit('details', details.value);
        } else {
          errors.value.push('Voucher/Coupon invalid');
        }
        voucher.value = '';
      })
      .catch((error) => {
        const couponErrors = error?.data?.errors?.coupon;

        if (Array.isArray(couponErrors) && couponErrors.length > 0) {
          errors.value.push(...couponErrors);
        } else {
          errors.value.push('Voucher/Coupon could not be checked. Please try again.');
        }
      })
      .finally(() => {
        loading.value = false;
      });
};

const remove = (code) => {
  current.value = current.value.filter((c) => c !== code);
  details.value = details.value.filter((c) => c.code !== code);
  model.value = current.value;
  emit('details', details.value.length > 0 ? details.value : false);
};

// Preset voucher logic
const maybePresetVoucher = () => {
  if (!options.value?.presetCoupon) return;
  if (props.items.length < 1) return;

  const valid = props.items.some((t) => {
    if (!t) return false;

    return (
        moment(options.value.presetCouponFrom).isBefore(moment(t)) &&
        moment(options.value.presetCouponTo).isAfter(moment(t))
    );
  });

  if (valid) {
    voucher.value = options.value.presetCoupon;
    check();
  } else {
    remove(options.value.presetCoupon);
  }
};

// Watchers
watch(() => props.items, maybePresetVoucher, { deep: true });

// Initialization
onMounted(() => {
  check();
});
</script>

<template>
  <div class="py-4">
    <div>
      <a
          href="#"
          @click.prevent="toggleOpen"
          class="font-bold flex items-center justify-start gap-2"
      >
        <span>Add a voucher code</span>
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="transition ease-in-out delay-150"
            :class="{'rotate-180': isOpen}"
        >
          <path
              d="M11.1973 16.2274L4.34783 9.37792C3.88406 8.94983 3.88406 8.20066 4.34783 7.77257C4.77592 7.3088 5.52508 7.3088 5.95318 7.77257L12.0178 13.8016L18.0468 7.77257C18.4749 7.3088 19.2241 7.3088 19.6522 7.77257C20.1159 8.20066 20.1159 8.94983 19.6522 9.37792L12.8027 16.2274C12.3746 16.6912 11.6254 16.6912 11.1973 16.2274Z"
              fill="currentColor"
          />
        </svg>
      </a>
    </div>
    <Transition name="fade">
      <div v-if="isOpen" class="voucher-field mb-4">
        <label>Voucher/Coupon</label>
        <div class="flex flex-grow gap-4 w-full lg:w-2/3 bg-light lg:bg-transparent">
          <input
              v-model="voucher"
              type="text"
              name="voucher"
              :disabled="loading"
              class="w-full rounded-sm border bg-white lg:bg-light shadow-inner border-light focus:border-transparent focus:ring-2 focus:ring-primary focus:ring-inset focus:bg-white disabled:cursor-wait disabled:opacity-70"
              :class="{ 'border-[#F54E4E]': errors.length }"
          />
          <button
              :disabled="loading"
              :aria-busy="loading ? 'true' : 'false'"
              class="inline-flex items-center justify-center gap-2 bg-dark text-light whitespace-nowrap rounded-full px-4 py-2 disabled:cursor-wait disabled:opacity-70"
              @click.prevent="check"
          >
            <svg
                v-if="loading"
                class="h-4 w-4 animate-spin"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
            >
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" d="M4 12a8 8 0 018-8" stroke="currentColor" stroke-width="4" stroke-linecap="round"></path>
            </svg>
            <span>{{ loading ? 'Checking...' : 'Add Code' }}</span>
          </button>
        </div>
        <div v-if="loading" class="mt-2 text-sm text-dark/70" role="status" aria-live="polite">
          Checking voucher/coupon...
        </div>
        <div v-if="errors.length" class="text-[#F54E4E] text-sm" v-html="errors.join('<br>')"></div>

        <ul v-if="details.length > 0">
          <li
              v-for="(detail, ind) in details"
              :key="`details-${ind}`"
              class="flex justify-between items-center text-sm"
          >
            <div class="py-2">
              <div>
                <strong class="block" v-text="detail.code"></strong>
              </div>
              <div v-text="human(detail)"></div>
            </div>
            <button
                class="button button-sm button-primary rounded-full"
                @click.prevent="() => remove(detail.code)"
            >
              X
            </button>
          </li>
        </ul>
      </div>
    </Transition>
  </div>
</template>
